import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, Select, Textarea, VStack,
  HStack, useToast, Badge, Box, Text, Table, Thead, Tbody, Tr, Th, Td,
  NumberInput, NumberInputField,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { PagoOP, obtenerPagosOP, registrarPagoOP } from '../../../../api/opTramiteApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  idTramite: number;
  folioTramite: string;
}

const ESTATUS_COLORS: Record<string, string> = {
  PENDIENTE: 'yellow', PAGADO: 'green', CANCELADO: 'red', DEVUELTO: 'orange',
};

const ModalPagosOP: React.FC<Props> = ({ isOpen, onClose, idTramite, folioTramite }) => {
  const [pagos, setPagos] = useState<PagoOP[]>([]);
  const [modo, setModo] = useState<'lista' | 'nuevo'>('lista');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const [form, setForm] = useState({
    concepto: '', monto: '', metodoPago: '', banco: '',
    referencia: '', folioRecibo: '', observaciones: '',
  });

  const cargar = async () => {
    try {
      const data = await obtenerPagosOP(idTramite);
      setPagos(data);
    } catch {
      toast({ title: 'Error al cargar pagos', status: 'error' });
    }
  };

  useEffect(() => {
    if (isOpen) { cargar(); setModo('lista'); }
  }, [isOpen]);

  const set = (f: string, v: string) => setForm(prev => ({ ...prev, [f]: v }));

  const handleGuardar = async () => {
    if (!form.concepto || !form.monto) {
      toast({ title: 'Concepto y monto son obligatorios', status: 'error' });
      return;
    }
    setLoading(true);
    try {
      await registrarPagoOP(idTramite, { ...form, monto: parseFloat(form.monto) as any });
      toast({ title: 'Pago registrado', status: 'success' });
      cargar();
      setModo('lista');
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const total = pagos.reduce((s, p) => s + Number(p.monto), 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          Pagos — {folioTramite}
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody pt={4}>
          {modo === 'lista' ? (
            <Box>
              <HStack mb={3} justify="space-between">
                <Text fontWeight="bold">Total: ${total.toFixed(2)} MXN</Text>
                <Button size="sm" leftIcon={<AddIcon />} colorScheme="green" onClick={() => setModo('nuevo')}>
                  Agregar Pago
                </Button>
              </HStack>

              {pagos.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={6}>No hay pagos registrados</Text>
              ) : (
                <Table size="sm" variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Concepto</Th>
                      <Th isNumeric>Monto</Th>
                      <Th>Método</Th>
                      <Th>Folio Recibo</Th>
                      <Th>Estatus</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pagos.map(p => (
                      <Tr key={p.id}>
                        <Td>{p.concepto}</Td>
                        <Td isNumeric>${Number(p.monto).toFixed(2)}</Td>
                        <Td>{p.metodoPago || '—'}</Td>
                        <Td fontFamily="mono">{p.folioRecibo || '—'}</Td>
                        <Td>
                          <Badge colorScheme={ESTATUS_COLORS[p.estatus] || 'gray'}>{p.estatus}</Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack>
                <Button size="sm" variant="outline" onClick={() => setModo('lista')}>← Regresar</Button>
                <Text fontWeight="bold">Nuevo Pago</Text>
              </HStack>

              <FormControl isRequired>
                <FormLabel>Concepto</FormLabel>
                <Input value={form.concepto} onChange={e => set('concepto', e.target.value)}
                  placeholder="Ej. Derechos de Licencia de Construcción" />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Monto (MXN)</FormLabel>
                <NumberInput min={0}>
                  <NumberInputField value={form.monto} onChange={e => set('monto', e.target.value)} />
                </NumberInput>
              </FormControl>

              <HStack>
                <FormControl>
                  <FormLabel>Método de Pago</FormLabel>
                  <Select value={form.metodoPago} onChange={e => set('metodoPago', e.target.value)}>
                    <option value="">Seleccione...</option>
                    <option value="EFECTIVO">Efectivo</option>
                    <option value="TRANSFERENCIA">Transferencia</option>
                    <option value="TARJETA">Tarjeta</option>
                    <option value="CHEQUE">Cheque</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Banco</FormLabel>
                  <Input value={form.banco} onChange={e => set('banco', e.target.value)} />
                </FormControl>
              </HStack>

              <HStack>
                <FormControl>
                  <FormLabel>Referencia / No. de Operación</FormLabel>
                  <Input value={form.referencia} onChange={e => set('referencia', e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Folio de Recibo</FormLabel>
                  <Input value={form.folioRecibo} onChange={e => set('folioRecibo', e.target.value)} />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Observaciones</FormLabel>
                <Textarea value={form.observaciones} onChange={e => set('observaciones', e.target.value)} rows={2} />
              </FormControl>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid #eee">
          <Button variant="ghost" mr={3} onClick={onClose}>Cerrar</Button>
          {modo === 'nuevo' && (
            <Button bg="#5b182b" color="white" _hover={{ bg: '#8b1f33' }} onClick={handleGuardar} isLoading={loading}>
              Registrar Pago
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalPagosOP;
